// Admin -> partner-viewer impersonation helpers.
//
// The flow:
//   1. Admin clicks "View as viewer" on PartnerDetail.
//   2. Frontend calls POST /auth/impersonate-partner-viewer/:partnerId.
//   3. Backend returns a fresh JWT scoped to the target partner with role=
//      PARTNER_VIEWER + impersonatedBy=<admin id>.
//   4. We stash the CURRENT admin token in sessionStorage (so Exit can restore
//      it), install the impersonated token in the cookie that auth/request use,
//      and reload the app at /partner/orders.
//   5. The ImpersonationBanner reads the impersonatedBy claim and shows a top
//      banner with "Exit impersonation" — which restores the stashed admin
//      token, calls /auth/exit-impersonation for audit, and navigates back to
//      the partner detail page.
//
// Reading the JWT payload client-side is fine — it's signed (not encrypted)
// and we only use the claims to *display* state, never to make auth decisions.

import Cookies from 'js-cookie';
import { TOKEN_NAME } from '../constants';
import { setSessionCookie } from './session';

const RESTORE_KEY = 'ovo-impersonation-restore-v1';

export function decodeJwtPayload(token) {
  if (!token) return null;
  try {
    const part = token.split('.')[1];
    if (!part) return null;
    const padded = part + '='.repeat((4 - (part.length % 4)) % 4);
    const json = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getCurrentImpersonation() {
  const token = Cookies.get(TOKEN_NAME);
  const payload = decodeJwtPayload(token);
  if (payload?.impersonatedBy) {
    return {
      impersonatedByEmail: payload.impersonatedByEmail || null,
      partnerId: payload.partnerId,
      exp: payload.exp,
    };
  }
  return null;
}

export function startImpersonation({ adminToken, impersonatedToken, returnPath }) {
  try {
    sessionStorage.setItem(
      RESTORE_KEY,
      JSON.stringify({ adminToken, returnPath: returnPath || null, savedAt: Date.now() }),
    );
  } catch {
    // sessionStorage full / blocked — Exit will fail gracefully (admin can re-login)
  }
  Cookies.set(TOKEN_NAME, impersonatedToken);
}

export function readRestore() {
  try {
    const raw = sessionStorage.getItem(RESTORE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearRestore() {
  try {
    sessionStorage.removeItem(RESTORE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Restore the admin token stashed before impersonation began.
 * Returns the path the caller asked to land back on, or null if the restore
 * couldn't be done (in which case the caller should send the user to /login).
 */
export function finishImpersonation() {
  const restore = readRestore();
  if (!restore?.adminToken) {
    Cookies.remove(TOKEN_NAME);
    clearRestore();
    return { restored: false, returnPath: null };
  }
  setSessionCookie(restore.adminToken);
  const path = restore.returnPath;
  clearRestore();
  return { restored: true, returnPath: path };
}
